import { CountryCodesConst } from '@axiom/const';
import { TaxonomyUtil } from '@axiom/ui';
import type {
  Account,
  AfcSubmission,
  ProjectNeedsWithLegalSkills,
  Taxonomy,
} from '@axiom/validation';
import { ProjectNeedsSchema } from '@axiom/validation';

const { CountryCodesAbbreviations } = CountryCodesConst;

const getMatchesUrl = ({
  accountId,
  submissionId,
}: {
  accountId: Account['id'];
  submissionId: AfcSubmission['id'];
}) => `/account/${accountId}/submission/${submissionId}/matches`;

/**
 * The skills the intake already holds, grouped to line up with `filteredSkillGroups` - the form
 * holds one answer per group, so the shape has to match even where a group contributes nothing.
 */
const addPrefilledLegalSkills = ({
  filteredSkillGroups,
  intakeLegalSkills,
  legalSkills,
}: {
  filteredSkillGroups: ReturnType<typeof TaxonomyUtil.getFocusAreaSkillGroups>;
  intakeLegalSkills: ProjectNeedsWithLegalSkills['legalSkills'];
  legalSkills: Taxonomy['legalSkills'];
}) => {
  return intakeLegalSkills
    ? filteredSkillGroups.map(({ key }) =>
        TaxonomyUtil.getLegalSkillOptions(legalSkills, key)
          .filter(({ value }) => intakeLegalSkills.includes(value))
          .map(({ value }) => value)
      )
    : [];
};

/*
  A missing country counts as inside: the address components only carry one once a place has been
  picked, and a remote opportunity never picks one.
*/
const isInsideUnitedStates = ({
  addressCountryCode,
  companyLocation,
}: {
  addressCountryCode?: ProjectNeedsWithLegalSkills['addressCountryCode'];
  companyLocation?: ProjectNeedsWithLegalSkills['companyLocation'] | null;
}) => {
  const addresses = companyLocation?.locationAddressComponents?.addresses || [];
  const countrySegment = addresses.filter(address =>
    address.types.includes('country')
  );
  const country =
    countrySegment.length > 0 ? countrySegment[0]?.short_name : null;
  const acceptedUSCountryCodes = new Set([null, CountryCodesAbbreviations.US]);

  return (
    acceptedUSCountryCodes.has(addressCountryCode ?? null) &&
    acceptedUSCountryCodes.has(country)
  );
};

/** Drops the form's own fields, which the endpoint does not take, when resetting or submitting. */
const removeDynamicFields = ({ data }: { data: object }) => {
  const schemaKeys = new Set(Object.keys(ProjectNeedsSchema.strict().shape));

  return Object.fromEntries(
    Object.entries(data).filter(([key]) => schemaKeys.has(key))
  );
};

export const DirectEngagementsFormUtil = {
  addPrefilledLegalSkills,
  getMatchesUrl,
  isInsideUnitedStates,
  removeDynamicFields,
};
